<template>
  <div
    ref="rootEl"
    @click.stop="tryClose"
    data-notify="container"
    class="alert alert-notify alert-dismissible d-inline-flex w-100"
    :class="classElement"
    :style="customPosition"
    role="alert"
    data-notify-position="top-center"
  >
    <template v-if="icon || $slots.icon">
      <slot name="icon">
        <span class="alert-icon flex-shrink-0" data-notify="icon">
          <i :class="icon"></i>
          <font-awesome-icon :icon="icon" />
        </span>
      </slot>
    </template>

    <span class="alert-text flex-grow-1">
      <span v-if="title" class="title">
        <b>{{ title }}<br /></b>
      </span>
      <span v-if="message" v-html="message" />
      <content-render v-if="displayComponent" :component="component" />
    </span>

    <slot name="dismiss-icon">
      <button
        type="button"
        class="close"
        data-dismiss="alert"
        aria-label="Close"
        @click.stop="close"
      >
        <span aria-hidden="true">×</span>
      </button>
    </slot>
  </div>
</template>
<script>
import {
  ref,
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUpdated,
} from "vue";
import { size } from "@/helpers/functions/ArrayHelpers";
import { useRoot } from "@/composables/useRoot";

const DEFAULT_NOTIFICATION_HEIGHT = 72;
const STACK_GAP = 8;

export default defineComponent({
  name: "notification",
  components: {
    contentRender: {
      props: ["component"],
      render: function (createElement) {
        return createElement(this.component);
      },
    },
  },
  emits: ["close"],
  props: {
    message: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      description: "Notification title",
    },
    icon: {
      type: String,
      description: "Notification icon",
    },
    verticalAlign: {
      type: String,
      default: "top",
      validator: (value) => {
        let acceptedValues = ["top", "bottom"];
        return acceptedValues.indexOf(value) !== -1;
      },
      description: "Vertical alignment of notification (top|bottom)",
    },
    horizontalAlign: {
      type: String,
      default: "right",
      validator: (value) => {
        let acceptedValues = ["left", "center", "right"];
        return acceptedValues.indexOf(value) !== -1;
      },
      description: "Horizontal alignment of notification (left|center|right)",
    },
    type: {
      type: String,
      default: "info",
      validator: (value) => {
        let acceptedValues = [
          "default",
          "info",
          "primary",
          "danger",
          "warning",
          "success",
        ];
        return acceptedValues.indexOf(value) !== -1;
      },
      description:
        "Notification type of notification (default|info|primary|danger|warning|success)",
    },
    timeout: {
      type: Number,
      default: 5000,
      validator: (value) => {
        return value >= 0;
      },
      description:
        "Notification timeout (closes after X milliseconds). Default is 5000 (5s)",
    },
    timestamp: {
      type: Date,
      default: () => new Date(),
      description:
        "Notification timestamp (used internally to handle notification removal correctly)",
    },
    component: {
      type: [Object, Function],
      description:
        "Custom content component. Cane be a `.vue` component or render function",
    },
    showClose: {
      type: Boolean,
      default: true,
      description: "Whether to show close button",
    },
    closeOnClick: {
      type: Boolean,
      default: true,
      description: "Whether to close notification when clicking it' body",
    },
    clickHandler: {
      type: Function,
      description: "Custom notification click handler",
    },
    count: {
      type: Number,
      default: 0,
    },
    stackIndex: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const rootEl = ref(null);
    const elementHeight = ref(DEFAULT_NOTIFICATION_HEIGHT);
    const $root = useRoot();

    const hasIcon = computed(() => size(props.icon) > 0);
    const alertType = computed(() => `alert-${props.type}`);
    const displayComponent = computed(() => !props.message && props.component);
    const stackOffset = computed(
      () => props.stackIndex * (elementHeight.value + STACK_GAP)
    );

    const classElement = computed(() => [
      { "alert-with-icon": props.icon },
      props.verticalAlign,
      props.horizontalAlign,
      alertType.value,
    ]);

    const customPosition = computed(() => {
      const alignDirection =
        props.verticalAlign === "bottom" ? "bottom" : "top";

      return {
        [alignDirection]: `${stackOffset.value}px`,
      };
    });

    function close() {
      emit("close", props.timestamp);
    }

    function tryClose(event) {
      props.clickHandler?.(event, $root);

      if (props.closeOnClick) close();
    }

    function updateElementHeight() {
      nextTick(() => {
        elementHeight.value =
          rootEl.value?.offsetHeight || DEFAULT_NOTIFICATION_HEIGHT;
      });
    }

    onMounted(() => {
      if (props.timeout) setTimeout(close, props.timeout);
      updateElementHeight();
    });

    onUpdated(updateElementHeight);

    return {
      rootEl,
      hasIcon,
      alertType,
      classElement,
      customPosition,
      displayComponent,
      close,
      tryClose,
    };
  },
});
</script>
<style lang="scss">
.notifications .alert {
  position: absolute;
  z-index: 10000;
  min-width: 300px;
  width: 100%;
  margin: 0;
  pointer-events: auto;
  padding-right: 2.5rem;
  transition:
    top 0.2s ease,
    bottom 0.2s ease,
    opacity 0.2s ease;

  &[data-notify="container"] {
    max-width: 500px;
  }

  &.center {
    margin: 0 auto;
  }
  &.left {
    left: 20px;
  }
  &.right {
    right: 20px;
  }
}
</style>
